import {
  CommentsCategory,
  type CommentDto,
  useAddComment,
  useDeleteComment,
  useEditComment
} from '@domain/comments'
import {useCurrentScheduleQuery, useScheduleItemQuery} from '@domain/schedule'
import {CardTitle} from '@hconnect/uikit/src/lib2'
import {useUpmPlantContext} from '@hconnect/upm'
import {Dialog, DialogContent} from '@mui/material'
import {
  useAssetHistoryDataByScheduleItemId,
  useHistoryStoragesByIdQuery,
  useScheduleCommentsByScheduleItemId,
  useSelectedScheduleItemId
} from '@widgets/planning-schedule-chart'
import {Moment} from 'moment-timezone'
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../../consts'
import {useTrackPlannerEvent} from '../../../helpers'
import {useDeleteScheduleItem, useUpdateScheduleItem} from '../../../hooks'

import {EditScheduleItemForm} from './EditScheduleItemForm'

export interface EditScheduleItemProps {
  startOfPlan: Moment
  endOfPlan: Moment
}

export const EditScheduleItemDialog: React.FC<EditScheduleItemProps> = ({
  startOfPlan,
  endOfPlan
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {scheduleItemId, setScheduleItemId} = useSelectedScheduleItemId()
  const {plantId: plantCode} = useUpmPlantContext()
  const trackPlannerEvent = useTrackPlannerEvent()
  const {data: schedule} = useCurrentScheduleQuery({
    plantId: plantCode,
    range: [startOfPlan, endOfPlan]
  })
  const historyAssetsByItemId = useAssetHistoryDataByScheduleItemId({
    schedule,
    timeFrame: [startOfPlan, endOfPlan]
  })

  const {updateScheduleItem, isUpdateScheduleItemLoading} = useUpdateScheduleItem({
    startOfPlan,
    endOfPlan,
    assetDataByScheduleItemId: historyAssetsByItemId
  })
  const deleteScheduleItem = useDeleteScheduleItem({startOfPlan, endOfPlan})

  const addCommentMutation = useAddComment(
    {plantId: plantCode, commentsCategory: CommentsCategory.AssetOperationTimes},
    'comments',
    {onSettled: () => trackPlannerEvent({name: 'onCommentAddToScheduleItem'})}
  )
  const editCommentMutation = useEditComment(
    {plantId: plantCode, commentsCategory: CommentsCategory.AssetOperationTimes},
    'comments',
    {onSettled: () => trackPlannerEvent({name: 'onCommentEditToScheduleItem'})}
  )
  const deleteCommentMutation = useDeleteComment(
    {plantId: plantCode, commentsCategory: CommentsCategory.AssetOperationTimes},
    'comments',
    {onSettled: () => trackPlannerEvent({name: 'onCommentDeleteToScheduleItem'})}
  )

  const addScheduleComment = useCallback(
    (
      params: {commentCategoryInstanceId: string; value: string},
      options?: {onSuccess?: () => void}
    ) => {
      addCommentMutation.mutate(
        {commentCategoryInstanceId: params.commentCategoryInstanceId, value: params.value},
        {onSuccess: options?.onSuccess}
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addCommentMutation.mutate]
  )

  const updateScheduleComment = useCallback(
    (params: {commentCategoryInstanceId: string; commentId: string; value: string}) => {
      editCommentMutation.mutate({
        commentCategoryInstanceId: params.commentCategoryInstanceId,
        id: params.commentId,
        value: params.value
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editCommentMutation.mutate]
  )

  const deleteComment = useCallback(
    (
      params: {commentCategoryInstanceId: string; commentId: string},
      options?: {onSuccess?: () => void; onError?: () => void}
    ) => {
      deleteCommentMutation.mutate(
        {commentCategoryInstanceId: params.commentCategoryInstanceId, id: params.commentId},
        {onSuccess: options?.onSuccess, onError: options?.onError}
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteCommentMutation.mutate]
  )

  const {data: storagesById} = useHistoryStoragesByIdQuery({
    timeFrame: [startOfPlan, endOfPlan],
    throwOnError: false
  })
  const {data: scheduleItem} = useScheduleItemQuery({
    plantId: plantCode,
    id: scheduleItemId,
    range: [startOfPlan, endOfPlan]
  })
  const scheduleItemIds = scheduleItemId ? [scheduleItemId] : []
  const {data: commentsByItemId, isLoading: isCommentsLoading} =
    useScheduleCommentsByScheduleItemId(scheduleItemIds, !scheduleItemId)
  if (!scheduleItem || !historyAssetsByItemId || !storagesById) return null
  const commentDto = commentsByItemId?.[scheduleItem.id]
  const comment: CommentDto | undefined = commentDto
  const assetName = historyAssetsByItemId[scheduleItem.id].asset.name

  return (
    <Dialog
      open={!!scheduleItem}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return
        }
        setScheduleItemId(undefined)
      }}
      fullWidth
    >
      <DialogContent>
        <CardTitle>{t('planning.planAsset', {asset: assetName})}</CardTitle>
        <EditScheduleItemForm
          scheduleItem={scheduleItem}
          startOfPlan={startOfPlan}
          endOfPlan={endOfPlan}
          comment={comment}
          isCommentsLoading={isCommentsLoading}
          assetData={historyAssetsByItemId[scheduleItem.id]}
          storagesById={storagesById}
          updateScheduleItem={updateScheduleItem}
          isUpdateScheduleItemLoading={isUpdateScheduleItemLoading}
          deleteScheduleItem={deleteScheduleItem}
          deleteComment={deleteComment}
          isDeletingComment={deleteCommentMutation.isPending}
          addScheduleComment={addScheduleComment}
          isAddCommentLoading={addCommentMutation.isPending}
          updateScheduleComment={updateScheduleComment}
          isUpdateCommentLoading={editCommentMutation.isPending}
        />
      </DialogContent>
    </Dialog>
  )
}
